import {
  useState, useCallback, useEffect, useRef,
} from 'react';
import { differenceInSeconds, addSeconds } from 'common-date/arithmetic';

/**
 * Coordinates a timeout from last MFA code sent
 * with an animated countdown
 *
 * No tests here since `PhoneResendMFA` tests the countdown functionality
 * but this live in `hooks` so that various views can import the function
 * that sets up the timeout
 */

const KEY = 'mfaTimeoutExpiration';
const DURATION = 15; // seconds

// Get the current time in ISO format
const getExpiration = () => addSeconds(new Date(), DURATION).toISOString();

// Set the last time the MFA code was sent in SessionStorage.
// ContactForm and PhoneUpdate can call this to set up the timeout
// which will take over when they unload
export const setMFATimeoutExpiration = () => sessionStorage.setItem(KEY, getExpiration());

// Basic hook to use the SessionStorage entry as state
const useMFATimeoutExpiration = () => {
  const [expiration, setExpiration] = useState(sessionStorage.getItem(KEY));
  return [
    expiration,
    useCallback((t = getExpiration()) => {
      sessionStorage.setItem(KEY, t);
      setExpiration(t);
    }, []),
  ];
};

// Does some date math to determine whether a countdown should be displayed
// and drives the rendering for the caller component
const useMFATimeout = () => {
  const rafId = useRef(null);
  const [expiration, setExpiration] = useMFATimeoutExpiration();

  const getTimeRemaining = useCallback(() => differenceInSeconds(
    new Date(expiration),
    new Date(),
    { roundingMethod: 'ceil' },
  ), [expiration]);

  // Calculate synchronously (on mount) so we don't get a flash
  // before the asynchronous countdown takes over
  // when useEffect is called
  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining());

  const helper = useCallback(() => {
    const newTimeRemaining = getTimeRemaining();
    setTimeRemaining(newTimeRemaining);

    if (!newTimeRemaining) {
      // MFA timeout has elapsed
      return;
    }

    rafId.current = requestAnimationFrame(helper);
  }, [getTimeRemaining]);

  useEffect(() => {
    rafId.current = requestAnimationFrame(helper);
    return () => cancelAnimationFrame(rafId.current);
  }, [helper]);

  return [
    timeRemaining,
    setExpiration,
  ];
};

export default useMFATimeout;
